/**
 * Поиск в попапе: история запросов и живой поиск товаров.
 *
 * Хранение истории: localStorage, ключ `carrotsbox_search_history`, формат `string[]`.
 * Последние 12 уникальных запросов, новые — в начале.
 */
(function () {
	'use strict';

	const STORAGE_KEY = 'carrotsbox_search_history';
	const MAX_ITEMS = 12;
	const MIN_CHARS = 2;
	const DEBOUNCE_MS = 300;
	const CHIPS_SELECTOR = '#search-popup .search-history-chips';
	const RESULTS_SELECTOR = '#search-popup .search-results';
	const RESULTS_LIST_SELECTOR = '#search-popup .search-results__list';
	const RESULTS_COUNT_SELECTOR = '#search-popup .search-results__count';
	const RESULTS_LINK_SELECTOR = '#search-popup .search-results__link';
	const RESULTS_ACTION_SELECTOR = '#search-popup .search-results__action';
	const RESULTS_EMPTY_SELECTOR = '#search-popup .search-results__empty';
	const INPUT_SELECTOR = '#search-popup .search-popup__input';

	let debounceTimer = null;
	let activeRequest = null;

	const escapeHtml = (value) => {
		const node = document.createElement('div');
		node.textContent = value ?? '';
		return node.innerHTML;
	};

	const getHistory = () => {
		try {
			const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
			return Array.isArray(data) ? data.filter((item) => typeof item === 'string' && item.trim()) : [];
		} catch {
			return [];
		}
	};

	const saveHistory = (history) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
	};

	const buildSearchUrl = (query) => {
		const url = new URL('/search/', window.location.origin);

		if (query) {
			url.searchParams.set('search', query);
		}

		return url.pathname + url.search;
	};

	const addToHistory = (query) => {
		const normalized = query.trim();

		if (!normalized) {
			return;
		}

		const history = getHistory().filter((item) => item !== normalized);
		history.unshift(normalized);
		saveHistory(history.slice(0, MAX_ITEMS));
	};

	const getPopupElements = () => ({
		chips: document.querySelector(CHIPS_SELECTOR),
		results: document.querySelector(RESULTS_SELECTOR),
		list: document.querySelector(RESULTS_LIST_SELECTOR),
		count: document.querySelector(RESULTS_COUNT_SELECTOR),
		link: document.querySelector(RESULTS_LINK_SELECTOR),
		action: document.querySelector(RESULTS_ACTION_SELECTOR),
		empty: document.querySelector(RESULTS_EMPTY_SELECTOR),
		input: document.querySelector(INPUT_SELECTOR),
	});

	const renderHistory = (container) => {
		// const el = container || document.querySelector(CHIPS_SELECTOR);
		const el = false;

		if (!el) {
			return;
		}

		const history = getHistory();

		if (!history.length) {
			el.innerHTML = '';
			el.hidden = true;
			return;
		}

		el.hidden = false;
		el.innerHTML = history
			.map(
				(query) =>
					`<button type="button" class="search-history-chips__item">${escapeHtml(query)}</button>`
			)
			.join('');
	};

	const hideResults = () => {
		const { chips, results, list, count, link, action, empty } = getPopupElements();

		if (results) {
			results.hidden = true;
			results.classList.remove('is-loading');
		}

		if (list) {
			list.innerHTML = '';
			list.hidden = false;
		}

		if (count) {
			count.textContent = '';
		}

		if (link) {
			link.href = buildSearchUrl('');
		}

		if (action) {
			action.hidden = false;
		}

		if (empty) {
			empty.hidden = true;
		}

		renderHistory(chips);
	};

	const showResults = ({ html, total, search_url: searchUrl, isEmpty = false }) => {
		const { chips, results, list, count, link, action, empty } = getPopupElements();

		if (!results || !list) {
			return;
		}

		if (chips) {
			chips.innerHTML = '';
			chips.hidden = true;
		}

		list.innerHTML = isEmpty ? '' : html || '';
		list.hidden = Boolean(isEmpty);
		results.hidden = false;
		results.classList.remove('is-loading');

		if (!isEmpty && window.CarrotsBoxCatalogImages) {
			window.CarrotsBoxCatalogImages.enqueue(list);
		}

		if (count) {
			count.textContent = total > 0 ? `(${total})` : '';
		}

		if (link) {
			link.href = searchUrl || buildSearchUrl('');
		}

		if (action) {
			action.hidden = Boolean(isEmpty);
		}

		if (empty) {
			empty.hidden = !isEmpty;
		}
	};

	const fetchResults = (query) => {
		if (!window.themeAjax?.ajaxUrl) {
			return Promise.resolve(null);
		}

		if (activeRequest) {
			activeRequest.abort();
		}

		activeRequest = new AbortController();

		const body = new URLSearchParams({
			action: 'search_popup',
			nonce: window.themeAjax.searchNonce || '',
			search: query,
		});

		return fetch(window.themeAjax.ajaxUrl, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
			},
			body: body.toString(),
			signal: activeRequest.signal,
		})
			.then((response) => response.json())
			.then((payload) => (payload?.success ? payload.data : null))
			.catch((error) => {
				if (error.name !== 'AbortError') {
					return null;
				}

				return undefined;
			})
			.finally(() => {
				activeRequest = null;
			});
	};

	const performSearch = async (query) => {
		const normalized = query.trim();
		const { results } = getPopupElements();

		if (!normalized || normalized.length < MIN_CHARS) {
			hideResults();
			return;
		}

		if (results) {
			results.hidden = false;
			results.classList.add('is-loading');
		}

		const data = await fetchResults(normalized);

		if (data === undefined) {
			return;
		}

		const input = document.querySelector(INPUT_SELECTOR);

		if (!input || input.value.trim() !== normalized) {
			return;
		}

		if (!data || !data.total) {
			showResults({
				total: 0,
				search_url: buildSearchUrl(normalized),
				isEmpty: true,
			});
			return;
		}

		showResults(data);
	};

	const reset = () => {
		clearTimeout(debounceTimer);

		if (activeRequest) {
			activeRequest.abort();
			activeRequest = null;
		}

		const { input } = getPopupElements();

		if (input) {
			input.value = '';
		}

		hideResults();
	};

	const saveCurrentPageQuery = () => {
		const path = window.location.pathname.replace(/\/+$/, '');
		const isSearchPage = path === '/search' || path.endsWith('/search');

		if (!isSearchPage) {
			return;
		}

		const query = new URLSearchParams(window.location.search).get('search')?.trim();

		if (query) {
			addToHistory(query);
		}
	};

	const init = () => {
		saveCurrentPageQuery();
		renderHistory();

		document.addEventListener('click', (event) => {
			const chip = event.target.closest('.search-history-chips__item');

			if (!chip?.closest('#search-popup')) {
				return;
			}

			const query = chip.textContent?.trim();

			if (!query) {
				return;
			}

			const { input } = getPopupElements();

			if (input) {
				input.value = query;
			}

			clearTimeout(debounceTimer);
			performSearch(query);
		});

		document.addEventListener('input', (event) => {
			if (!event.target.matches('.search-popup__input')) {
				return;
			}

			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(() => {
				performSearch(event.target.value);
			}, DEBOUNCE_MS);
		});

		document.addEventListener('submit', (event) => {
			const form = event.target.closest('.search-popup__form');

			if (!form) {
				return;
			}

			const input = form.querySelector('.search-popup__input');
			const query = input?.value?.trim();

			if (query) {
				addToHistory(query);
			}
		});
	};

	document.addEventListener('DOMContentLoaded', init);

	window.CarrotsBoxSearch = {
		getHistory,
		add: addToHistory,
		render: renderHistory,
		reset,
		search: performSearch,
	};
})();
